import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Globe2,
  Ship,
  Anchor,
  Clock,
  AlertCircle,
  Plus,
  Package,
  TriangleAlert,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Input } from '~/components/ui/inputs/Input';
import { PortSelector } from '~/components/common/PortSelector';
import { DeliveryMethodSelector } from '~/components/common/DeliveryMethodSelector';
import { DeliveryJourneyDiagram } from '~/components/common/DeliveryJourneyDiagram';
import { useGetShippingRoutes, useRequestShippingRoute } from '~/api/logistics';
import { updateCartTransport } from '~/api/cartTransport';
import { useLatestEdit } from '~/hooks/useLatestEdit';
import {
  LogisticsIncoterm,
  PortType,
  ShippingRouteStatus,
  type DeliveryMethod,
  type GetCartsWithFilterQuery,
  type InternationalShippingInput,
  type Port,
  type ShippingRoute,
} from '~/graphql/graphql';
import type { RootState } from '~/redux/store';
import type { DomesticDeliveryMethod } from '~/types/general';
import type { VehicleOption } from '~/utils/vehicleGroups';
import {
  originIsDoor,
  destinationIsDoor,
  shouldClearLegPort,
} from '~/utils/deliveryMethod';
import { isComboIncoterm, incotermRequiresAddress } from '~/utils/incoterms';
import {
  isCartQuery,
  patchCartCaches,
  patchCartCachesDroppingStaleReads,
  restoreCartCaches,
} from '~/utils/cartCache';
import { formatMoney } from '~/utils';

export type PortLite = Pick<
  Port,
  'id' | 'code' | 'name' | 'city' | 'country' | 'type'
>;
type RouteLite = Pick<
  ShippingRoute,
  | 'id'
  | 'status'
  | 'forwarder'
  | 'hasForwarderContact'
  | 'transitDays'
  | 'freeDays'
  | 'freightCost'
  | 'incotermLoading'
  | 'incotermDischarge'
>;

interface InternationalShippingSectionProps {
  cartId: string;
  deliveryMethod: DeliveryMethod | null | undefined;
  incoTerm: LogisticsIncoterm | null | undefined;
  incotermDischarge: LogisticsIncoterm | null | undefined;
  incotermAddressDestination: string | null | undefined;
  portOfLoading: PortLite | null | undefined;
  portOfDischarge: PortLite | null | undefined;
  shippingRoute: RouteLite | null | undefined;
  sellerNearestPort: PortLite | null | undefined;
  buyerNearestPort: PortLite | null | undefined;
  buyerFacilityAddress: string | null;
  deliveryAddressText?: string | null;
  isSubOrder?: boolean;
  containerVehicles: VehicleOption[];
  selectedTransportVehicleId: string;
  onTransportChange: (
    deliveryMethod: DomesticDeliveryMethod,
    transportVehicleId: string,
    vehicleType?: VehicleOption['vehicleType']
  ) => void;
}

const COMBO_PORT_TYPES: PortType[] = [
  PortType.Cy,
  PortType.Ramp,
  PortType.Door,
];

// Legacy Cart.incoTerm is null on a fresh international cart (no Pickup/Delivered toggle
// to seed it); the mutation requires a non-null value. Real incoterms live in incotermLoading/Discharge.
const INTERNATIONAL_LEGACY_INCOTERM_FALLBACK: LogisticsIncoterm = 'DAP';

export function InternationalShippingSection({
  cartId,
  deliveryMethod,
  incoTerm,
  incotermDischarge: savedIncotermDischarge,
  incotermAddressDestination: savedAddress,
  portOfLoading: savedPortOfLoading,
  portOfDischarge: savedPortOfDischarge,
  shippingRoute: savedShippingRoute,
  sellerNearestPort,
  buyerNearestPort,
  buyerFacilityAddress,
  deliveryAddressText = null,
  isSubOrder = false,
  containerVehicles,
  selectedTransportVehicleId,
  onTransportChange,
}: Readonly<InternationalShippingSectionProps>) {
  const { t } = useTranslation('cart');
  const { mutate: requestShippingRoute } = useRequestShippingRoute();
  const qc = useQueryClient();
  const { begin: beginEdit, isLatest: isLatestEdit } = useLatestEdit();
  const allPorts = useSelector((state: RootState) => state.auth.ports);

  // Saved props are the single source of truth for the fields below; mutations write
  // an optimistic patch into the cart caches which then propagates to Redux via Cart.tsx.
  const portOfLoadingId = savedPortOfLoading?.id ?? '';
  const portOfDischargeId = savedPortOfDischarge?.id ?? '';
  const selectedRouteId = savedShippingRoute?.id ?? '';

  // Address is buffered locally and persisted on blur (per-keystroke mutations would be too
  // chatty). The effect below re-seeds from savedAddress only when the field isn't focused.
  const [address, setAddress] = useState(savedAddress ?? '');
  const addressFocusedRef = useRef(false);
  const [requestMode, setRequestMode] = useState(false);

  useEffect(() => {
    if (!addressFocusedRef.current) {
      setAddress(savedAddress ?? '');
    }
  }, [savedAddress]);

  const loadingPortTypes =
    deliveryMethod && originIsDoor(deliveryMethod)
      ? COMBO_PORT_TYPES
      : [PortType.Seaport];
  const dischargePortTypes =
    deliveryMethod && destinationIsDoor(deliveryMethod)
      ? COMBO_PORT_TYPES
      : [PortType.Seaport];

  const selectionsComplete = Boolean(
    deliveryMethod && portOfLoadingId && portOfDischargeId
  );

  const routeFilter = useMemo(
    () =>
      selectionsComplete
        ? {
            portOfLoadingId,
            portOfDischargeId,
            status: ShippingRouteStatus.Active,
          }
        : undefined,
    [selectionsComplete, portOfLoadingId, portOfDischargeId]
  );

  const { data: routesData, isLoading: routesLoading } = useGetShippingRoutes(
    0,
    20,
    routeFilter,
    undefined,
    { enabled: selectionsComplete }
  );
  const matchingRoutes = useMemo(() => {
    const raw = routesData?.shippingRoutes?.content ?? [];
    const legMatches = (r: (typeof raw)[number]) =>
      !deliveryMethod ||
      (isComboIncoterm(r.incotermLoading) === originIsDoor(deliveryMethod) &&
        isComboIncoterm(r.incotermDischarge) ===
          destinationIsDoor(deliveryMethod));
    return [...raw].filter(legMatches).sort((a, b) => {
      const ac = a.freightCost?.amount;
      const bc = b.freightCost?.amount;
      if (ac == null && bc == null) return 0;
      if (ac == null) return 1;
      if (bc == null) return -1;
      return ac - bc;
    });
  }, [routesData, deliveryMethod]);

  type CartListCart = NonNullable<GetCartsWithFilterQuery['carts']>[number];
  type OptimisticPatch = Pick<
    CartListCart,
    | 'deliveryMethod'
    | 'incotermLoading'
    | 'incotermDischarge'
    | 'incotermAddressDestination'
    | 'portOfLoading'
    | 'portOfDischarge'
    | 'shippingRoute'
  >;
  type MutationVars = {
    incoTerm: LogisticsIncoterm;
    transportVehicleId: string | null;
    internationalShipping: InternationalShippingInput;
    optimisticPatch: OptimisticPatch;
  };

  const findCachedCart = (): CartListCart | undefined => {
    const found = qc
      .getQueriesData<unknown>({ predicate: isCartQuery })
      .map(([, value]) => {
        const v = value as
          | { cart?: CartListCart | null }
          | { carts?: CartListCart[] | null }
          | undefined;
        if (!v) return undefined;
        if ('cart' in v && v.cart?.id === cartId) return v.cart;
        if ('carts' in v && Array.isArray(v.carts))
          return v.carts.find((c) => c.id === cartId);
        return undefined;
      })
      .find((c): c is CartListCart => !!c);
    return found;
  };

  // Direct useMutation so onMutate can write the optimistic patch. The cart queries are
  // intentionally NOT invalidated — a refetch between rapid clicks would overwrite an
  // in-flight commit.
  const updateCart = useMutation({
    mutationFn: (vars: MutationVars) =>
      updateCartTransport({
        cartId,
        incoTerm: vars.incoTerm,
        transportVehicleId: vars.transportVehicleId,
        internationalShipping: vars.internationalShipping,
      }),
    onMutate: async (vars: MutationVars) => {
      const edit = beginEdit();
      await qc.cancelQueries({ predicate: isCartQuery });
      const snapshots = patchCartCaches(qc, cartId, (cart: CartListCart) => ({
        ...cart,
        ...vars.optimisticPatch,
      }));
      return { snapshots, edit };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshots && isLatestEdit(ctx.edit)) {
        restoreCartCaches(qc, ctx.snapshots);
      }
      toast.error(err.message);
    },
    onSuccess: async (data, _vars, ctx) => {
      // Port / ShippingRoute stay as the optimistic patch wrote them; the next cart
      // refetch canonically rehydrates those shapes.
      const confirmed = data.updateCartTransport;
      if (!confirmed) return;
      if (!isLatestEdit(ctx.edit)) return;
      await patchCartCachesDroppingStaleReads(
        qc,
        cartId,
        (cart: CartListCart) => ({
          ...cart,
          deliveryMethod: confirmed.deliveryMethod,
          incotermLoading: confirmed.incotermLoading,
          incotermDischarge: confirmed.incotermDischarge,
          incotermAddressDestination: confirmed.incotermAddressDestination,
        })
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['shippingCost'] });
    },
  });

  // Hydrate a port id to the full Port shape the cart query expects. Redux is the
  // primary source; fallbacks cover the case where a port id originated outside
  // that list (e.g. a facility's nearestPort the user just defaulted to).
  const resolvePort = (
    id: string,
    ...fallbacks: Array<PortLite | null | undefined>
  ): PortLite | null => {
    if (!id) return null;
    const fromRedux = allPorts.find((p) => p.id === id);
    if (fromRedux) return fromRedux;
    for (const fb of fallbacks) {
      if (fb && fb.id === id) return fb;
    }
    return null;
  };

  // Merge base comes from the cache (not closure props): between rapid clicks the cache
  // reflects the prior optimistic write while this render's saved* props are still stale.
  const persist = (delta: {
    deliveryMethod?: DeliveryMethod;
    incotermAddressDestination?: string;
    portOfLoadingId?: string;
    portOfDischargeId?: string;
    shippingRoute?: RouteLite | null;
  }) => {
    const cachedCart = findCachedCart();

    const baseAddress = cachedCart?.incotermAddressDestination ?? '';
    const basePortOfLoadingId = cachedCart?.portOfLoading?.id ?? '';
    const basePortOfDischargeId = cachedCart?.portOfDischarge?.id ?? '';
    const baseRoute: RouteLite | null = cachedCart?.shippingRoute ?? null;

    const nextAddress =
      delta.incotermAddressDestination !== undefined
        ? delta.incotermAddressDestination
        : baseAddress;
    const nextPortOfLoadingId =
      delta.portOfLoadingId !== undefined
        ? delta.portOfLoadingId
        : basePortOfLoadingId;
    const nextPortOfDischargeId =
      delta.portOfDischargeId !== undefined
        ? delta.portOfDischargeId
        : basePortOfDischargeId;
    const nextRoute =
      delta.shippingRoute !== undefined ? delta.shippingRoute : baseRoute;
    const nextDeliveryMethod =
      delta.deliveryMethod !== undefined
        ? delta.deliveryMethod
        : (cachedCart?.deliveryMethod ?? deliveryMethod ?? null);

    const internationalShipping: InternationalShippingInput = {
      // Incoterms are sourced from the route on the server; the client never sets them.
      incotermLoading: null,
      incotermDischarge: null,
      incotermAddressDestination: nextAddress || null,
      portOfLoadingId: nextPortOfLoadingId || null,
      portOfDischargeId: nextPortOfDischargeId || null,
      shippingRouteId: nextRoute?.id ?? null,
      deliveryMethod: nextDeliveryMethod,
    };

    const optimisticPatch: OptimisticPatch = {
      deliveryMethod: nextDeliveryMethod,
      incotermLoading: nextRoute?.incotermLoading ?? null,
      incotermDischarge: nextRoute?.incotermDischarge ?? null,
      incotermAddressDestination:
        internationalShipping.incotermAddressDestination,
      portOfLoading: resolvePort(
        nextPortOfLoadingId,
        cachedCart?.portOfLoading ?? savedPortOfLoading,
        sellerNearestPort
      ),
      portOfDischarge: resolvePort(
        nextPortOfDischargeId,
        cachedCart?.portOfDischarge ?? savedPortOfDischarge,
        buyerNearestPort
      ),
      shippingRoute: nextRoute,
    };

    updateCart.mutate({
      incoTerm: incoTerm ?? INTERNATIONAL_LEGACY_INCOTERM_FALLBACK,
      transportVehicleId: selectedTransportVehicleId || null,
      internationalShipping,
      optimisticPatch,
    });
  };

  // Pre-populate ports + address from the facilities the first time the section
  // mounts on a cart that hasn't picked them yet. Persists so the defaults
  // survive reload and are visible on checkout. The address is set even when
  // the current discharge incoterm doesn't render it — switching to a method
  // whose destination is a door reveals the pre-filled value.
  const didDefaultsRef = useRef(false);
  useEffect(() => {
    if (didDefaultsRef.current) return;
    const needLoading = !savedPortOfLoading?.id && sellerNearestPort?.id;
    const needDischarge = !savedPortOfDischarge?.id && buyerNearestPort?.id;
    const needAddress = !savedAddress && buyerFacilityAddress;
    if (!needLoading && !needDischarge && !needAddress) return;
    didDefaultsRef.current = true;
    persist({
      ...(needLoading ? { portOfLoadingId: sellerNearestPort!.id } : {}),
      ...(needDischarge ? { portOfDischargeId: buyerNearestPort!.id } : {}),
      ...(needAddress
        ? { incotermAddressDestination: buyerFacilityAddress! }
        : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMethodChange = (method: DeliveryMethod) => {
    const cached = findCachedCart();
    if (method === (cached?.deliveryMethod ?? deliveryMethod)) return;
    const clearLoading = shouldClearLegPort(
      cached?.portOfLoading?.type,
      originIsDoor(method)
    );
    const clearDischarge = shouldClearLegPort(
      cached?.portOfDischarge?.type,
      destinationIsDoor(method)
    );
    persist({
      deliveryMethod: method,
      ...(clearLoading ? { portOfLoadingId: '' } : {}),
      ...(clearDischarge ? { portOfDischargeId: '' } : {}),
      shippingRoute: null,
    });
  };

  const handleLoadingPortChange = (_n: string, value: string) => {
    persist({ portOfLoadingId: value, shippingRoute: null });
  };

  const handleDischargePortChange = (_n: string, value: string) => {
    persist({ portOfDischargeId: value, shippingRoute: null });
  };

  const handleAddressChange = (_n: string, value: string) => {
    setAddress(value);
  };

  const handleAddressFocus = () => {
    addressFocusedRef.current = true;
  };

  const handleAddressBlur = () => {
    addressFocusedRef.current = false;
    // Trim before persisting so whitespace-only input doesn't get stored as a
    // real address (the backend's StringUtils.hasText check rejects it at
    // placeOrder, and the frontend completeness check trims too). Also reflect
    // the trim back into local state so the field shows the cleaned value.
    const trimmed = address.trim();
    if (trimmed !== address) setAddress(trimmed);
    if (trimmed !== (savedAddress ?? '')) {
      persist({ incotermAddressDestination: trimmed });
    }
  };

  const handleRouteSelect = (route: RouteLite) => {
    persist({ shippingRoute: route });
  };

  const handleContainerChange = (vehicleId: string) => {
    const vehicle = containerVehicles.find((v) => v.id === vehicleId);
    onTransportChange('delivered', vehicleId, vehicle?.vehicleType);
  };

  const handleRequestRoute = (forwarder: string, notes: string) => {
    if (!selectionsComplete || !deliveryMethod) return;
    requestShippingRoute(
      {
        input: {
          portOfLoadingId,
          portOfDischargeId,
          forwarder: forwarder || null,
          notes: notes || null,
        },
      },
      {
        onSuccess: (result) => {
          setRequestMode(false);
          persist({ shippingRoute: result.requestShippingRoute });
          toast.success(t('cart.international.route_request_submitted'));
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  // Pin a saved route the ACTIVE query doesn't return (e.g. a REQUESTED one) so the
  // buyer sees their pending request instead of being offered "request new route".
  const pinnedRoute =
    savedShippingRoute &&
    !matchingRoutes.some((route) => route.id === savedShippingRoute.id)
      ? savedShippingRoute
      : null;
  const isPinnedPending = pinnedRoute?.status === ShippingRouteStatus.Requested;

  return (
    <section
      className="bg-neut-0 p-3 flex flex-col gap-3 rounded-lg border border-prim-100"
      data-testid="international-shipping-section"
    >
      <h4 className="text-sm text-neut-prim font-medium flex items-center gap-1.5">
        <Globe2 className="w-4 h-4 text-prim-600" />
        {t('cart.international.title')}
      </h4>

      {/* Container */}
      <div className="space-y-1.5">
        <LegHeader
          icon={<Package className="w-3 h-3 text-prim-600" />}
          title={t('cart.container')}
        />
        {containerVehicles.length === 0 && (
          <div className="rounded-md border border-warning-300 bg-warning-50 p-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
            <p className="text-xs font-medium text-neut-prim">
              {t('cart.international.no_container_vehicles')}
            </p>
          </div>
        )}
        <select
          data-testid="transport-vehicle-select"
          aria-label={t('cart.container')}
          value={selectedTransportVehicleId}
          onChange={(e) => handleContainerChange(e.target.value)}
          className="w-full text-xs border border-neut-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-prim-500"
        >
          <option value="">{t('cart.select_transport_mode')}</option>
          {containerVehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>

      {/* Delivery method */}
      <div className="p-3 bg-neut-0 rounded-lg border border-neut-200 space-y-3">
        <p className="text-xs font-medium text-neut-700">
          {t('cart.international.delivery_terms')}
        </p>
        <DeliveryMethodSelector
          value={deliveryMethod ?? null}
          onChange={handleMethodChange}
        />
        {deliveryMethod && <DeliveryJourneyDiagram method={deliveryMethod} />}
      </div>

      {!deliveryMethod && (
        <p className="text-xs text-neut-sec flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" />
          {t('cart.international.pick_method_first')}
        </p>
      )}

      {deliveryMethod && (
        <>
          {/* Loading leg */}
          <div className="space-y-1.5">
            <LegHeader
              icon={<Ship className="w-3 h-3 text-prim-600" />}
              title={t('cart.international.loading_side')}
            />
            <PortSelector
              name="portOfLoadingId"
              label={
                originIsDoor(deliveryMethod)
                  ? t('cart.international.origin_point')
                  : t('cart.international.port_of_loading')
              }
              value={portOfLoadingId}
              onChange={handleLoadingPortChange}
              types={loadingPortTypes}
              pinned={savedPortOfLoading ?? null}
            />
          </div>

          {/* Discharge leg */}
          <div className="space-y-1.5">
            <LegHeader
              icon={<Anchor className="w-3 h-3 text-prim-600" />}
              title={t('cart.international.discharge_side')}
            />
            <PortSelector
              name="portOfDischargeId"
              label={
                destinationIsDoor(deliveryMethod)
                  ? t('cart.international.destination_point')
                  : t('cart.international.port_of_discharge')
              }
              value={portOfDischargeId}
              onChange={handleDischargePortChange}
              types={dischargePortTypes}
              pinned={savedPortOfDischarge ?? null}
            />
            {incotermRequiresAddress(savedIncotermDischarge ?? null) &&
              isSubOrder && (
                <div>
                  <span className="text-xs font-medium text-neut-700">
                    {t('cart.international.delivery_address')}
                  </span>
                  <div
                    className="p-3 bg-neut-0 rounded-lg border border-neut-200 mt-1"
                    data-testid="incoterm-address-destination-readonly"
                  >
                    <p className="text-sm text-neut-700">
                      {buyerFacilityAddress ||
                        t('cart.international.address_placeholder')}
                    </p>
                  </div>
                  <p className="text-xs text-neut-sec mt-1 italic">
                    {t('cart.international.delivery_address_readonly_hint')}
                  </p>
                </div>
              )}
            {incotermRequiresAddress(savedIncotermDischarge ?? null) &&
              !isSubOrder &&
              deliveryAddressText && (
                <div>
                  <span className="text-xs font-medium text-neut-700">
                    {t('cart.international.delivery_address')}
                  </span>
                  <div
                    className="p-3 bg-neut-0 rounded-lg border border-neut-200 mt-1"
                    data-testid="incoterm-address-from-delivery-address"
                  >
                    <p className="text-sm text-neut-700">
                      {deliveryAddressText}
                    </p>
                  </div>
                  <p className="text-xs text-neut-sec mt-1 italic">
                    {t('cart.delivery_address.from_delivery_address')}
                  </p>
                </div>
              )}
            {incotermRequiresAddress(savedIncotermDischarge ?? null) &&
              !isSubOrder &&
              !deliveryAddressText && (
                <Input
                  name="incotermAddressDestination"
                  label={t('cart.international.delivery_address')}
                  value={address}
                  onChange={handleAddressChange}
                  onFocus={handleAddressFocus}
                  onBlur={handleAddressBlur}
                  placeholder={t('cart.international.address_placeholder')}
                />
              )}
          </div>
        </>
      )}

      {/* Routes */}
      <div className="border-t border-neut-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-semibold text-neut-prim">
            {t('cart.international.shipping_route')}
          </h5>
          {selectionsComplete && !routesLoading && (
            <span className="text-[10px] text-neut-sec">
              {t('cart.international.matches', {
                count: matchingRoutes.length,
              })}
            </span>
          )}
        </div>

        {!selectionsComplete && (
          <p className="text-xs text-neut-sec flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" />
            {t('cart.international.pick_to_see_routes')}
          </p>
        )}

        {selectionsComplete && routesLoading && (
          <p className="text-xs text-neut-sec">
            {t('cart.international.loading_routes')}
          </p>
        )}

        {selectionsComplete && !routesLoading && (
          <div className="space-y-1.5">
            {pinnedRoute && isPinnedPending && (
              <div
                data-testid="shipping-route-pinned"
                className="rounded-md border border-warning-300 bg-warning-50 p-2 flex items-start gap-2"
              >
                <Clock className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-neut-prim">
                    {pinnedRoute.forwarder
                      ? `${pinnedRoute.forwarder} — ${t('cart.international.pending_review')}`
                      : t('cart.international.pending_review')}
                  </p>
                  <p className="text-[10px] text-neut-sec mt-0.5">
                    {t('cart.international.pending_review_hint')}
                  </p>
                </div>
              </div>
            )}

            {pinnedRoute && !isPinnedPending && (
              <RouteOption
                route={pinnedRoute}
                isSelected={selectedRouteId === pinnedRoute.id}
                statusLabel={t('cart.international.selected')}
                statusClass="text-success-700"
                testId="shipping-route-pinned"
                onSelect={() => handleRouteSelect(pinnedRoute)}
              />
            )}

            {matchingRoutes.map((route) => {
              const isSelected = selectedRouteId === route.id;
              return (
                <RouteOption
                  key={route.id}
                  route={route}
                  isSelected={isSelected}
                  statusLabel={
                    isSelected
                      ? t('cart.international.selected')
                      : t('cart.international.active')
                  }
                  statusClass="text-success-700"
                  onSelect={() => handleRouteSelect(route)}
                />
              );
            })}

            {matchingRoutes.length === 0 && !pinnedRoute && !requestMode && (
              <div className="rounded-md border border-dashed border-warning-300 bg-warning-50 p-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neut-prim">
                    {t('cart.international.no_matching_route')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setRequestMode(true)}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-prim-700 hover:text-prim-800"
                  >
                    <Plus className="w-3 h-3" />
                    {t('cart.international.request_new_route')}
                  </button>
                </div>
              </div>
            )}

            {requestMode && (
              <RequestRouteForm
                onCancel={() => setRequestMode(false)}
                onSubmit={handleRequestRoute}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function LegHeader({
  icon,
  title,
}: Readonly<{ icon: React.ReactNode; title: string }>) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neut-sec">
      {icon}
      {title}
    </div>
  );
}

function RouteOption({
  route,
  isSelected,
  statusLabel,
  statusClass,
  testId = 'shipping-route-option',
  onSelect,
}: Readonly<{
  route: RouteLite;
  isSelected: boolean;
  statusLabel: string;
  statusClass: string;
  testId?: string;
  onSelect: () => void;
}>) {
  const { t } = useTranslation('cart');

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className={[
        'w-full text-left rounded-md border px-2 py-1.5 transition-colors',
        'flex items-center justify-between gap-2',
        isSelected
          ? 'border-prim-500 bg-prim-50 ring-1 ring-prim-200'
          : 'border-neut-200 bg-neut-0 hover:bg-neut-50',
      ].join(' ')}
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-neut-prim truncate">
          {route.forwarder ?? '—'}
          {route.hasForwarderContact === false && (
            <span
              data-testid={`route-${route.id}-no-contact`}
              role="img"
              aria-label={t('cart.international.no_forwarder_contact')}
              title={t('cart.international.no_forwarder_contact')}
              className="ml-1.5 text-amber-600"
            >
              <TriangleAlert size={12} className="inline" aria-hidden="true" />
            </span>
          )}
        </p>
        <p className="text-[10px] text-neut-sec flex items-center gap-1 mt-0.5">
          <Clock className="w-2.5 h-2.5" />
          {route.transitDays != null
            ? t('cart.international.transit_days', { days: route.transitDays })
            : t('cart.international.transit_unknown')}
          {route.freeDays != null && (
            <>
              <span aria-hidden="true">·</span>
              {t('cart.international.free_days', { days: route.freeDays })}
            </>
          )}
        </p>
      </div>
      <div className="text-right shrink-0">
        {route.freightCost && (
          <p className="text-xs font-semibold text-neut-prim">
            {formatMoney(route.freightCost)}
          </p>
        )}
        <p
          className={`text-[9px] uppercase tracking-wide font-semibold ${statusClass}`}
        >
          {statusLabel}
        </p>
      </div>
    </button>
  );
}

function RequestRouteForm({
  onCancel,
  onSubmit,
}: Readonly<{
  onCancel: () => void;
  onSubmit: (forwarder: string, notes: string) => void;
}>) {
  const { t } = useTranslation('cart');
  const [forwarder, setForwarder] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div className="space-y-2 rounded-md border border-neut-200 bg-neut-50 p-2">
      <p className="text-xs font-semibold text-neut-prim">
        {t('cart.international.request_form_title')}
      </p>
      <Input
        name="forwarder"
        label={t('cart.international.forwarder_optional')}
        value={forwarder}
        onChange={(_n: string, v: string) => setForwarder(v)}
        placeholder={t('cart.international.forwarder_placeholder')}
      />
      <Input
        name="notes"
        label={t('cart.international.notes')}
        value={notes}
        onChange={(_n: string, v: string) => setNotes(v)}
        placeholder={t('cart.international.notes_placeholder')}
      />
      <div className="flex items-center gap-1.5 justify-end pt-0.5">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-neut-sec hover:text-neut-prim px-2 py-1"
        >
          {t('cart.international.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onSubmit(forwarder, notes)}
          className="text-xs font-medium bg-prim-500 text-neut-0 hover:bg-prim-hover rounded-md px-3 py-1"
        >
          {t('cart.international.submit_request')}
        </button>
      </div>
    </div>
  );
}
